import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/apiConstants';
import { getStoredTokens, getUserInfo, isAuthenticated } from '../utils/auth';
import './HelpAssistant.css';

const HELP_GREETING = 'Hi, I am your Adflyn Help Assistant. Ask me about this page, where to find features, or what to do next.';

const QUICK_QUESTIONS = [
    'What does this page do?',
    'How do I get started?',
    'Where can I generate images?'
];

const FALLBACK_ISSUE_OPTIONS = [
    {
        code: 'login-access',
        title: 'Login or account access issue',
        summary: 'Cannot sign in, token keeps expiring, or page access is blocked.',
        category: 'bug',
        priority: 'high'
    },
    {
        code: 'chatbot-response',
        title: 'AI chatbot reply issue',
        summary: 'Chatbot times out, errors, or does not respond.',
        category: 'bug',
        priority: 'high'
    },
    {
        code: 'web-analyzer-scan',
        title: 'Web scanner analysis issue',
        summary: 'Scan fails or returns incomplete website insights.',
        category: 'bug',
        priority: 'medium'
    },
    {
        code: 'gallery-generation',
        title: 'Gallery generation/upload issue',
        summary: 'Poster generation or image upload is not working.',
        category: 'bug',
        priority: 'medium'
    },
    {
        code: 'social-connection',
        title: 'Social account connection issue',
        summary: 'Meta account connect flow fails or analytics are missing.',
        category: 'question',
        priority: 'medium'
    },
    {
        code: 'calendar-scheduling',
        title: 'Calendar scheduling issue',
        summary: 'Unable to create, edit, duplicate, or delete planned posts.',
        category: 'bug',
        priority: 'medium'
    },
    {
        code: 'profile-account',
        title: 'Profile or account data issue',
        summary: 'Profile details or connected account data look incorrect.',
        category: 'question',
        priority: 'medium'
    },
    {
        code: 'billing-subscription',
        title: 'Billing or subscription query',
        summary: 'Payment, invoice, or subscription support request.',
        category: 'billing',
        priority: 'medium'
    }
];

const createMessage = (text, sender) => ({
    id: `${sender}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`,
    text,
    sender
});

const HelpAssistant = () => {
    const location = useLocation();
    const messagesEndRef = useRef(null);
    const initialUserInfo = getUserInfo();

    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isReportingIssue, setIsReportingIssue] = useState(false);
    const [activeAction, setActiveAction] = useState('chat');
    const [messages, setMessages] = useState([createMessage(HELP_GREETING, 'assistant')]);
    const [lastTicket, setLastTicket] = useState(null);
    const [issueOptions, setIssueOptions] = useState(FALLBACK_ISSUE_OPTIONS);
    const [isLoadingIssueOptions, setIsLoadingIssueOptions] = useState(false);
    const [hasFetchedIssueOptions, setHasFetchedIssueOptions] = useState(false);

    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isOpen, messages, isLoading]);

    const buildJsonHeaders = () => {
        const tokens = getStoredTokens();
        const headers = {
            'Content-Type': 'application/json'
        };

        if (tokens?.idToken) {
            headers.Authorization = `Bearer ${tokens.idToken}`;
        }

        return headers;
    };

    const loadIssueOptions = async () => {
        if (isLoadingIssueOptions) {
            return;
        }

        setIsLoadingIssueOptions(true);
        try {
            const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.HELP_TICKET_OPTIONS}`, {
                method: 'GET',
                headers: buildJsonHeaders()
            });

            const data = await response.json();
            if (!response.ok || !Array.isArray(data.issues) || data.issues.length === 0) {
                throw new Error(data.message || data.error || 'Failed to load predefined issues');
            }

            setIssueOptions(data.issues);
        } catch (error) {
            console.error('Failed to load predefined help issues:', error);
            setIssueOptions(FALLBACK_ISSUE_OPTIONS);
        } finally {
            setIsLoadingIssueOptions(false);
        }
    };

    useEffect(() => {
        if (!isOpen || hasFetchedIssueOptions) {
            return;
        }

        setHasFetchedIssueOptions(true);
        loadIssueOptions();
    }, [isOpen, hasFetchedIssueOptions]);

    if (!isAuthenticated() || location.pathname === '/auth/callback') {
        return null;
    }

    const sendPrompt = async (rawPrompt) => {
        const prompt = rawPrompt.trim();
        if (!prompt || isLoading) return;

        setMessages((prev) => [...prev, createMessage(prompt, 'user')]);
        setInputValue('');
        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.HELP_CHATBOT}`, {
                method: 'POST',
                headers: buildJsonHeaders(),
                body: JSON.stringify({
                    message: prompt,
                    pagePath: location.pathname,
                    pageTitle: document.title,
                    pageUrl: window.location.href
                })
            });

            let data = {};
            try {
                data = await response.json();
            } catch (parseError) {
                data = {};
            }

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Failed to get help response');
            }

            const reply = data.reply || data.data?.reply;
            if (!reply) {
                throw new Error('Help assistant returned an empty response');
            }

            setMessages((prev) => [...prev, createMessage(reply, 'assistant')]);
        } catch (error) {
            setMessages((prev) => [
                ...prev,
                createMessage(
                    `I could not respond right now: ${error.message}. Please try again in a moment.`,
                    'assistant'
                )
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReportIssue = async (issue) => {
        if (!issue?.code || isReportingIssue) {
            return;
        }

        setMessages((prev) => [
            ...prev,
            createMessage(`Report issue: ${issue.title}`, 'user')
        ]);

        setIsReportingIssue(true);

        try {
            const createResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.HELP_TICKET}`, {
                method: 'POST',
                headers: buildJsonHeaders(),
                body: JSON.stringify({
                    issueCode: issue.code,
                    requesterEmail: initialUserInfo?.email || null,
                    pagePath: location.pathname,
                    pageTitle: document.title,
                    pageUrl: window.location.href,
                    metadata: {
                        channel: 'help_assistant_widget'
                    }
                })
            });

            const ticketData = await createResponse.json();
            if (!createResponse.ok) {
                throw new Error(ticketData.message || ticketData.error || 'Failed to create ticket');
            }

            const ticketId = ticketData.ticketId;

            let assignedEmail = ticketData.assignedEmail || null;
            let emailMessage = null;
            let emailError = null;

            try {
                const emailResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.HELP_TICKET_EMAIL}`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify({
                        ticketId,
                        requesterEmail: initialUserInfo?.email || null,
                        pagePath: location.pathname,
                        pageTitle: document.title,
                        pageUrl: window.location.href
                    })
                });

                const emailData = await emailResponse.json();
                if (!emailResponse.ok) {
                    throw new Error(emailData.message || emailData.error || 'Failed to send support email');
                }

                assignedEmail = emailData.assignedEmail || assignedEmail;
                emailMessage = emailData.messageId || null;
            } catch (sendErr) {
                emailError = sendErr.message;
            }

            setLastTicket({
                ticketId,
                requesterEmail: initialUserInfo?.email || null,
                assignedEmail,
                issueCode: issue.code
            });

            if (emailError) {
                setMessages((prev) => [
                    ...prev,
                    createMessage(
                        `Ticket ${ticketId} created for "${issue.title}", but support email failed: ${emailError}.`,
                        'assistant'
                    )
                ]);
            } else {
                const providerHint = emailMessage ? ` (message id: ${emailMessage})` : '';
                const recipientLabel = assignedEmail || 'configured support recipient';
                setMessages((prev) => [
                    ...prev,
                    createMessage(
                        `Ticket ${ticketId} created and sent to ${recipientLabel} via SES${providerHint}.`,
                        'assistant'
                    )
                ]);
            }

            setActiveAction('chat');
        } catch (error) {
            setMessages((prev) => [
                ...prev,
                createMessage(
                    `Issue report failed: ${error.message}`,
                    'assistant'
                )
            ]);
        } finally {
            setIsReportingIssue(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        await sendPrompt(inputValue);
    };

    const handleQuickQuestion = async (question) => {
        await sendPrompt(question);
    };

    return (
        <div className="help-assistant-root">
            {isOpen && (
                <div className="help-assistant-panel" role="dialog" aria-label="Adflyn Help Assistant">
                    <div className="help-assistant-header">
                        <div>
                            <h3>Page Help</h3>
                            <p>Stateless support for this page</p>
                        </div>
                        <button
                            type="button"
                            className="help-assistant-close"
                            onClick={() => setIsOpen(false)}
                            aria-label="Close help assistant"
                        >
                            x
                        </button>
                    </div>

                    <div className="help-assistant-messages">
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`help-assistant-message ${message.sender === 'user' ? 'user' : 'assistant'}`}
                            >
                                {message.text}
                            </div>
                        ))}

                        {isLoading && (
                            <div className="help-assistant-message assistant typing">
                                <span />
                                <span />
                                <span />
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    <div className="help-assistant-actions">
                        <button
                            type="button"
                            className={activeAction === 'chat' ? 'active' : ''}
                            onClick={() => setActiveAction('chat')}
                            disabled={isReportingIssue}
                        >
                            Ask Help
                        </button>
                        <button
                            type="button"
                            className={activeAction === 'issue' ? 'active' : ''}
                            onClick={() => setActiveAction('issue')}
                            disabled={isReportingIssue}
                        >
                            Report Issue
                        </button>
                    </div>

                    {activeAction === 'issue' && (
                        <div className="help-assistant-issue-panel">
                            <div className="help-assistant-ticket-hint">
                                Select one predefined issue. We will create a ticket and email support automatically.
                            </div>
                            {isLoadingIssueOptions ? (
                                <div className="help-assistant-ticket-hint">Loading issue options...</div>
                            ) : (
                                issueOptions.map((issue) => (
                                    <button
                                        type="button"
                                        key={issue.code}
                                        className="help-assistant-issue-option"
                                        onClick={() => handleReportIssue(issue)}
                                        disabled={isReportingIssue}
                                    >
                                        <span className="help-assistant-issue-title">{issue.title}</span>
                                        <span className="help-assistant-issue-summary">{issue.summary}</span>
                                        <span className="help-assistant-issue-meta">
                                            {issue.category || 'general'} • {issue.priority || 'medium'}
                                        </span>
                                    </button>
                                ))
                            )}
                            {lastTicket?.ticketId && (
                                <div className="help-assistant-ticket-hint">
                                    Last ticket: {lastTicket.ticketId}
                                </div>
                            )}
                        </div>
                    )}

                    {messages.length === 1 && activeAction === 'chat' && (
                        <div className="help-assistant-quick-actions">
                            {QUICK_QUESTIONS.map((question) => (
                                <button
                                    type="button"
                                    key={question}
                                    onClick={() => handleQuickQuestion(question)}
                                    disabled={isLoading}
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    )}

                    {activeAction === 'chat' && (
                        <form className="help-assistant-input" onSubmit={handleSubmit}>
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(event) => setInputValue(event.target.value)}
                                placeholder="Ask about this page..."
                                disabled={isLoading}
                            />
                            <button type="submit" disabled={isLoading || !inputValue.trim()}>
                                Send
                            </button>
                        </form>
                    )}
                </div>
            )}

            <button
                type="button"
                className="help-assistant-toggle"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label={isOpen ? 'Close help assistant' : 'Open help assistant'}
            >
                {isOpen ? 'x' : 'Help'}
            </button>
        </div>
    );
};

export default HelpAssistant;
